import { useContext, useEffect, useState } from "react";
import PackagePreview from "../../../components/productPackages/PackagePreview";
import { FieldLabel } from "../../../components/productPackages/FieldInfoTip";
import { PACKAGE_FIELD_HELP } from "../../../components/productPackages/fieldHelp";
import { formatMoneyInput, handleMoneyInputChange } from "../../../components/productPackages/moneyInput";
import { usePackageEditor } from "../../../components/productPackages/usePackageEditor";
import { gradingTypes, gradingTypeSupportsAutograph } from "../../../components/utils/gradingTypes";
import ToastContext from "../../../context/toastProvider";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileFormField from "../../ui/MobileFormField";
import { mobileInput, mobilePrimaryBtn, mobileSecondaryBtn } from "../../theme";

const fieldGridClass = "grid grid-cols-1 gap-3";
const checkboxClass = "h-5 w-5 rounded border-primary/50 bg-monao accent-primary";

const formatCents = (value) => new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
}).format(Number(value || 0) / 100);

function StepTitle({ step, title, hint }) {
  return (
    <div className="space-y-1">
      <p className="text-base font-semibold text-laranjinha">
        <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-primary text-[11px] text-primary">
          {step}
        </span>
        {title}
      </p>
      {hint ? <p className="text-xs text-laranjinha/60">{hint}</p> : null}
    </div>
  );
}

function ReferenceSummary({ quote, loading, savings, promoPriceCents }) {
  return (
    <section className="rounded-lg border border-primary/30 bg-monao p-3 text-sm text-laranjinha/75">
      <div className="flex items-center justify-between gap-3">
        <p className="font-medium text-laranjinha">
          <FieldLabel help={PACKAGE_FIELD_HELP.referenceAnchor}>Âncora de preço</FieldLabel>
        </p>
        {loading ? <span className="text-xs text-laranjinha/60">Atualizando...</span> : null}
      </div>
      {quote ? (
        <div className="mt-2 space-y-1">
          <p>Preço cheio: {formatCents(quote.priceFullCents)}</p>
          <p>Com desconto por qtd: {formatCents(quote.priceWithQtyDiscountCents)}</p>
          <p>
            Economia:{" "}
            {savings
              ? `${formatCents(savings.savedCents)} (${savings.savedPercent}%)`
              : "defina preço promo menor"}
          </p>
          <p className="text-xs text-laranjinha/55">
            Promo atual: {formatCents(promoPriceCents)}. Prazo do pacote é o definido abaixo.
          </p>
        </div>
      ) : (
        <p className="mt-2 text-laranjinha/60">
          A cotação aparece automaticamente após montar as linhas de grading.
        </p>
      )}
    </section>
  );
}

function PackageSheet({ open, onClose, user, productPackage, onSaved }) {
  const { goodToast = () => null, badToast = () => null } = useContext(ToastContext);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const editor = usePackageEditor({ token: user?.token, productPackage });
  const {
    form,
    quote,
    savings,
    canGenerateCopy,
    imageFile,
    referenceLoading,
    generatingCopy,
    saving,
    uploadingImage,
    error,
    copyStatus,
    canAddItem,
    canRemoveItem,
    updateField,
    updateItem,
    addItem,
    removeItem,
    setImageFile,
    generateCopy,
    save,
  } = editor;
  const busy = saving || uploadingImage || generatingCopy;
  const title = productPackage ? "Editar pacote" : "Criar pacote";
  const previewImageUrl = imagePreviewUrl || form.imageUrl;
  const referencePriceCents = quote?.selected?.priceFullCents ?? quote?.priceFullCents;

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await save();
    if (result?.type === "success") {
      goodToast(productPackage ? "Pacote atualizado!" : "Pacote criado!");
      onSaved?.(result);
    } else {
      badToast(result?.message || "Erro ao salvar pacote.");
    }
  };

  return (
    <MobileFilterSheet open={open} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-xs text-laranjinha/65">
          Fluxo: oferta → condições → textos → publicação. A IA só gera copy depois da oferta e do preço.
        </p>

        <section className={fieldGridClass}>
          <StepTitle
            step={1}
            title="Oferta fechada"
            hint="Uma ou mais linhas de grading (ex.: 1 Premium + 5 sem subgrades). Prazo na etapa seguinte."
          />
          {(form.items || []).map((item, index) => (
            <div key={`mobile-item-${index}`} className="space-y-2 rounded-lg border border-primary/30 bg-monao p-3">
              <MobileFormField label={`Tipo #${index + 1}`} help={PACKAGE_FIELD_HELP.gradingType}>
                <select
                  aria-label={`Tipo ${index + 1}`}
                  value={item.gradingType}
                  onChange={(event) => updateItem(index, "gradingType", event.target.value)}
                  className={mobileInput}
                >
                  {Object.entries(gradingTypes).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </MobileFormField>
              <MobileFormField label="Quantidade de cartas" help={PACKAGE_FIELD_HELP.numberOfCards}>
                <input
                  type="number"
                  min="1"
                  aria-label={`Quantidade ${index + 1}`}
                  value={item.numberOfCards}
                  onChange={(event) => updateItem(index, "numberOfCards", event.target.value)}
                  className={mobileInput}
                />
              </MobileFormField>
              <label className="flex min-h-[44px] items-center gap-2 text-sm text-laranjinha/80">
                <input
                  type="checkbox"
                  checked={Boolean(item.autographEvaluation)}
                  disabled={!gradingTypeSupportsAutograph(item.gradingType)}
                  onChange={(event) => updateItem(index, "autographEvaluation", event.target.checked)}
                  className={checkboxClass}
                  aria-label={`Avaliação de autógrafo ${index + 1}`}
                />
                <FieldLabel help={PACKAGE_FIELD_HELP.autograph}>Avaliação de autógrafo</FieldLabel>
              </label>
              <button
                type="button"
                onClick={() => removeItem(index)}
                disabled={!canRemoveItem}
                className={mobileSecondaryBtn}
              >
                Remover linha
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addItem}
            disabled={!canAddItem}
            className={mobileSecondaryBtn}
          >
            + Adicionar tipo
          </button>
        </section>

        <section className={fieldGridClass}>
          <StepTitle
            step={2}
            title="Condições da promoção"
            hint="Preço e prazo fixos; compare com a âncora."
          />
          <MobileFormField label="Preço promocional" help={PACKAGE_FIELD_HELP.promoPrice}>
            <input
              type="text"
              inputMode="numeric"
              aria-label="Preço promocional"
              value={formatMoneyInput(form.promoPriceCents)}
              onChange={(event) => handleMoneyInputChange(
                event.target.value,
                (cents) => updateField("promoPriceCents", cents)
              )}
              className={mobileInput}
            />
          </MobileFormField>
          <MobileFormField label="Prazo promocional" help={PACKAGE_FIELD_HELP.promoDeadline}>
            <input
              type="number"
              min="1"
              aria-label="Prazo promocional"
              value={form.promoDeadlineBusinessDays}
              onChange={(event) => updateField("promoDeadlineBusinessDays", event.target.value)}
              className={mobileInput}
            />
          </MobileFormField>
          <ReferenceSummary
            quote={quote}
            loading={referenceLoading}
            savings={savings}
            promoPriceCents={form.promoPriceCents}
          />
        </section>

        <section className={fieldGridClass}>
          <StepTitle
            step={3}
            title="Textos da oferta"
            hint="Gere só depois de fechar oferta e condições."
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-laranjinha/60">
              {canGenerateCopy ? "Pronto para gerar com IA." : "Aguardando oferta + preço/prazo + referência."}
            </p>
            <button
              type="button"
              onClick={async () => {
                const result = await generateCopy();
                if (result?.type === "success") {
                  goodToast("Copy gerada — revise título e subtítulo.");
                } else if (result?.message) {
                  badToast(result.message);
                }
              }}
              disabled={!canGenerateCopy || generatingCopy}
              className={mobileSecondaryBtn}
            >
              {generatingCopy ? "Gerando..." : "Gerar IA"}
            </button>
          </div>
          {copyStatus ? (
            <p className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 px-3 py-2 text-sm text-emerald-200">
              {copyStatus}
            </p>
          ) : null}
          {error ? (
            <p className="rounded-lg border border-red-500/50 bg-red-950/40 px-3 py-2 text-sm text-red-200">
              {error}
            </p>
          ) : null}
          <MobileFormField label="Título" help={PACKAGE_FIELD_HELP.title}>
            <input
              type="text"
              aria-label="Título"
              value={form.title}
              onChange={(event) => updateField("title", event.target.value)}
              placeholder="Ex.: 10 Premium com economia real"
              className={mobileInput}
            />
          </MobileFormField>
          <MobileFormField label="Subtítulo" help={PACKAGE_FIELD_HELP.subtitle}>
            <textarea
              rows={3}
              aria-label="Subtítulo"
              value={form.subtitle}
              onChange={(event) => updateField("subtitle", event.target.value)}
              placeholder="Ex.: Pacote fechado com prazo promocional e desconto vs tabela."
              className={mobileInput}
            />
          </MobileFormField>
        </section>

        <section className={fieldGridClass}>
          <StepTitle step={4} title="Identidade e publicação" />
          <MobileFormField label="Nome interno" help={PACKAGE_FIELD_HELP.internalName}>
            <input
              type="text"
              aria-label="Nome interno"
              value={form.internalName}
              onChange={(event) => updateField("internalName", event.target.value)}
              placeholder="Pacote Premium 10 cartas"
              className={mobileInput}
            />
          </MobileFormField>
          <MobileFormField label="Slug" help={PACKAGE_FIELD_HELP.slug}>
            <input
              type="text"
              aria-label="Slug"
              value={form.slug}
              onChange={(event) => updateField("slug", event.target.value)}
              placeholder="pacote-premium-10"
              className={mobileInput}
            />
          </MobileFormField>
          <MobileFormField label="Início" help={PACKAGE_FIELD_HELP.startsAt}>
            <input
              type="date"
              aria-label="Início"
              value={form.startsAt}
              onChange={(event) => updateField("startsAt", event.target.value)}
              className={mobileInput}
            />
          </MobileFormField>
          <MobileFormField label="Fim" help={PACKAGE_FIELD_HELP.endsAt}>
            <input
              type="date"
              aria-label="Fim"
              value={form.endsAt}
              onChange={(event) => updateField("endsAt", event.target.value)}
              className={mobileInput}
            />
          </MobileFormField>
          <label className="flex min-h-[44px] items-center gap-2 text-sm text-laranjinha/80">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => updateField("isActive", event.target.checked)}
              className={checkboxClass}
              aria-label="Ativo"
            />
            <FieldLabel help={PACKAGE_FIELD_HELP.isActive}>Ativo na vigência</FieldLabel>
          </label>
        </section>

        <section className={fieldGridClass}>
          <StepTitle
            step={5}
            title="Visual (opcional)"
            hint="Sem imagem, o preview usa quantidade, economia e preço."
          />
          <MobileFormField label="Imagem opcional" help={PACKAGE_FIELD_HELP.image}>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              aria-label="Imagem opcional"
              onChange={(event) => setImageFile(event.target.files?.[0] || null)}
              className="w-full text-sm text-laranjinha file:mr-3 file:min-h-[44px] file:rounded-lg file:border-0 file:bg-primary file:px-3 file:text-mgs-bg"
            />
          </MobileFormField>
        </section>

        <details className="rounded-lg border border-primary/40 bg-monao p-3" open>
          <summary className="cursor-pointer text-sm font-semibold text-laranjinha">
            Preview do pacote
          </summary>
          <div className="mt-3">
            <PackagePreview
              title={form.title}
              subtitle={form.subtitle}
              items={form.items}
              promoPriceCents={form.promoPriceCents}
              promoDeadlineBusinessDays={form.promoDeadlineBusinessDays}
              imageUrl={previewImageUrl}
              referencePriceCents={referencePriceCents}
              savingsCents={savings?.savedCents}
              savingsPercent={savings?.savedPercent}
              viewport="mobile"
              surface="vitrine"
            />
          </div>
        </details>

        {error ? (
          <p className="rounded-lg border border-red-500 bg-red-950/40 p-3 text-sm text-red-200">
            {error}
          </p>
        ) : null}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={`flex-1 ${mobileSecondaryBtn}`}
          >
            Cancelar
          </button>
          <button type="submit" disabled={busy} className={`flex-1 ${mobilePrimaryBtn}`}>
            {saving || uploadingImage ? "Salvando..." : "Salvar pacote"}
          </button>
        </div>
      </form>
    </MobileFilterSheet>
  );
}

export function MobileCreatePackageSheet(props) {
  if (!props.open) return null;

  return <PackageSheet {...props} productPackage={null} />;
}

export function MobilePackageEditSheet(props) {
  if (!props.open || !props.productPackage) return null;

  return <PackageSheet {...props} />;
}
